import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { formatDate } from "../../lib/date";
import {
  Mail, Phone, StickyNote, Users, CheckCircle2, HeartPulse, Loader2,
  ChevronRight, ChevronDown, Plus, CalendarClock, MoreVertical, Trash2, Repeat,
} from "lucide-react";
import { Button } from "../ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuSeparator,
} from "../ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  note: { label: "Note", icon: StickyNote, fg: "#59554D", bg: "#F1EEE6" },
  email: { label: "Email", icon: Mail, fg: "#1D4ED8", bg: "#EAF1FF" },
  call: { label: "Call", icon: Phone, fg: "#0E6B3F", bg: "#E9F6EE" },
  meeting: { label: "Meeting", icon: Users, fg: "#8A6D00", bg: "#FFF8D6" },
  task: { label: "Task", icon: CheckCircle2, fg: "#8A6D00", bg: "#FFF8D6" },
  health_check: { label: "Health check", icon: HeartPulse, fg: "#C23A00", bg: "#FBEAE3" },
};

const monthKey = (d) => {
  if (!d) return "Undated";
  const dt = new Date(d);
  if (isNaN(dt)) return "Undated";
  return dt.toLocaleString("en-AU", { month: "long", year: "numeric" });
};

// filters: which kinds are offered depends on entity type
export default function ActivityTimeline({ entity, id, onLogTouch, onCompleteTask }) {
  const [data, setData] = useState({ upcoming: [], past: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState({});
  const [busyId, setBusyId] = useState(null);

  const base = entity === "client" ? `${API}/customer-success/clients` : `${API}/sales/prospects`;

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const r = await axios.get(`${base}/${encodeURIComponent(id)}/timeline`);
      setData({ upcoming: r.data.upcoming || [], past: r.data.past || [] });
    } catch (e) {
      toast.error("Failed to load activity");
    } finally {
      setLoading(false);
    }
  }, [base, id]);

  useEffect(() => { load(); }, [load]);

  const FILTERS = entity === "client"
    ? ["all", "note", "email", "call", "meeting", "health_check"]
    : ["all", "note", "email", "call", "meeting"];

  const match = (e) => filter === "all" || e.kind === filter || (filter !== "health_check" && e.kind === "task" && false);
  const past = data.past.filter(match);
  const upcoming = data.upcoming; // tasks always shown regardless of type filter

  // collapse email events sharing a thread (thread_id, else normalised subject) into one thread card
  const normSubj = (s) => (s || "").replace(/^\s*(re|fwd?)\s*:\s*/i, "").replace(/\s+/g, " ").trim().toLowerCase();
  const threadedPast = [];
  const threadIdx = new Map();
  for (const e of past) {
    if (e.kind === "email") {
      const key = e.thread_id || normSubj(e.subject || e.title) || "email";
      if (threadIdx.has(key)) threadedPast[threadIdx.get(key)].messages.push(e);
      else { threadIdx.set(key, threadedPast.length); threadedPast.push({ ...e, messages: [e] }); }
    } else threadedPast.push(e);
  }
  for (const e of threadedPast) {
    if (e.messages) {
      e.messages.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
      const head = e.messages[0];
      e.id = head.id; e.date = head.date; e.owner = head.owner;
      e.title = head.subject || head.title;
      e.count = e.messages.length;
    }
  }

  // group past by month (preserving desc order)
  const groups = [];
  threadedPast.forEach((e) => {
    const key = monthKey(e.date);
    let g = groups.find((x) => x.key === key);
    if (!g) { g = { key, items: [] }; groups.push(g); }
    g.items.push(e);
  });

  const toggle = (eid) => setExpanded((p) => ({ ...p, [eid]: !p[eid] }));

  const completeTask = async (e) => {
    if (!onCompleteTask) return;
    setBusyId(e.id);
    try {
      await onCompleteTask(e);
      await load();
    } finally {
      setBusyId(null);
    }
  };

  const changeType = async (e, kind) => {
    try {
      await axios.patch(`${base}/${encodeURIComponent(id)}/timeline/${e.id}`, null, { params: { kind } });
      toast.success("Type updated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change type");
    }
  };

  const removeEvent = async (e) => {
    if (!window.confirm("Remove this activity from the timeline? This cannot be undone.")) return;
    try {
      await axios.delete(`${base}/${encodeURIComponent(id)}/timeline/${e.id}`);
      toast.success("Activity removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not remove");
    }
  };

  const TYPE_OPTS = [
    { v: "note", label: "Note" }, { v: "call", label: "Call" },
    { v: "email", label: "Email" }, { v: "meeting", label: "Meeting" },
  ];

  const Card = ({ e }) => {
    const meta = KIND_META[e.kind] || KIND_META.note;
    const Icon = meta.icon;
    const isTask = e.kind === "task";
    const isEmail = e.kind === "email";
    const messages = e.messages || (isEmail ? [e] : null);
    const isThread = isEmail && messages && messages.length > 1;
    const open = expanded[e.id];
    const hasBody = isEmail
      ? (messages || []).some((m) => m.body_html || (m.body && m.body.trim()))
      : !!(e.body && e.body.trim());
    return (
      <div className="rounded-lg border border-[#E6E2D9] bg-white px-4 py-3" data-testid={`timeline-card-${e.id}`}>
        <div className="flex items-start gap-3">
          <div className="w-7 h-7 rounded-md flex items-center justify-center shrink-0 mt-0.5" style={{ background: meta.bg }}>
            <Icon className="w-4 h-4" style={{ color: meta.fg }} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {hasBody && (
                    <button onClick={() => toggle(e.id)} className="text-[#8A857A] hover:text-[#1A1A1A]" data-testid={`timeline-toggle-${e.id}`}>
                      {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                    </button>
                  )}
                  <span className="text-[10px] font-bold uppercase tracking-wide" style={{ color: meta.fg }}>{meta.label}</span>
                  {isThread && <span className="mono text-[10px] px-1.5 py-0.5 rounded bg-[#EAF1FF] text-[#1D4ED8] border border-[#D5E1F6]" data-testid={`timeline-thread-count-${e.id}`}>{e.count} messages</span>}
                  {e.owner && <span className="text-[12px] text-[#59554D]">· {e.owner}</span>}
                </div>
                <div className={`text-[13.5px] mt-0.5 ${isTask && e.done ? "line-through text-[#8A857A]" : "text-[#1A1A1A]"} ${!isTask ? "font-medium" : ""}`}>
                  {e.title}
                </div>
              </div>
              <div className="flex items-start gap-1.5 shrink-0">
              <span className="mono text-[11px] text-[#8A857A] shrink-0 whitespace-nowrap mt-0.5">{formatDate(e.date)}</span>
              {!isTask && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className="text-[#8A857A] hover:text-[#1A1A1A] shrink-0 -mr-1" data-testid={`timeline-menu-${e.id}`}>
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-white border-[#DAD5CA]">
                    {e.kind !== "health_check" && !isThread && (
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger data-testid={`timeline-changetype-${e.id}`}><Repeat className="w-3.5 h-3.5 mr-2" /> Change type</DropdownMenuSubTrigger>
                        <DropdownMenuSubContent className="bg-white border-[#DAD5CA]">
                          {TYPE_OPTS.map((o) => (
                            <DropdownMenuItem key={o.v} disabled={o.v === e.kind} onClick={() => changeType(e, o.v)} data-testid={`timeline-settype-${o.v}-${e.id}`}>{o.label}</DropdownMenuItem>
                          ))}
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                    )}
                    {e.kind !== "health_check" && !isThread && <DropdownMenuSeparator />}
                    <DropdownMenuItem onClick={() => removeEvent(e)} className="text-[#C23A00] focus:text-[#C23A00]" data-testid={`timeline-delete-${e.id}`}><Trash2 className="w-3.5 h-3.5 mr-2" /> {isThread ? "Delete latest" : "Delete"}</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
              </div>
            </div>
            {hasBody && open && (
              isEmail ? (
                <div className="mt-2 space-y-2" data-testid={`timeline-thread-${e.id}`}>
                  {messages.map((m) => (
                    <div key={m.id} className="rounded-md border border-[#EFEBE1] bg-[#FBFAF7] p-2.5" data-testid={`timeline-email-msg-${m.id}`}>
                      <div className="mono text-[10.5px] text-[#8A857A] mb-1">{formatDate(m.date)}{m.owner ? ` · ${m.owner}` : ""}</div>
                      {m.body_html
                        ? <div className="at-email-body" dangerouslySetInnerHTML={{ __html: m.body_html }} />
                        : (m.body && <p className="text-[13px] text-[#59554D] whitespace-pre-wrap">{m.body}</p>)}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-[13px] text-[#59554D] whitespace-pre-wrap mt-2 pl-0">{e.body}</p>
              )
            )}
            {isTask && !e.done && onCompleteTask && (
              <div className="mt-2">
                <Button size="sm" variant="ghost" disabled={busyId === e.id} onClick={() => completeTask(e)}
                  className="h-7 px-2 text-[#0E6B3F] hover:bg-[#E3F3E9]" data-testid={`timeline-complete-${e.id}`}>
                  {busyId === e.id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />} Mark done
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3" data-testid="activity-timeline">
      <style>{`.at-email-body{font-size:13px;line-height:1.55;color:#1A1A1A;word-break:break-word;overflow-wrap:anywhere}.at-email-body *{max-width:100%}.at-email-body img{max-width:100%;height:auto}.at-email-body a{color:#8A6D00}.at-email-body p{margin:0 0 8px}`}</style>
      {/* Toolbar */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`h-7 px-2.5 rounded-full text-[11px] font-semibold transition-colors ${filter === f ? "bg-[#1A1A1A] text-white" : "bg-white border border-[#DAD5CA] text-[#59554D] hover:bg-[#F4F2ED]"}`}
            data-testid={`timeline-filter-${f}`}>
            {f === "all" ? "All" : (KIND_META[f]?.label || f)}
          </button>
        ))}
        {onLogTouch && (
          <Button size="sm" onClick={onLogTouch} className="ml-auto h-7 bg-[#FFD108] hover:bg-[#E0B800] text-[#1A1A1A] text-[11px] font-semibold" data-testid="timeline-log-touch">
            <Plus className="w-3.5 h-3.5 mr-1" /> Log touch
          </Button>
        )}
      </div>

      {loading ? (
        <div className="space-y-2">{Array.from({ length: 4 }).map((_, i) => <div key={i} className="skeleton h-16 w-full rounded-lg" />)}</div>
      ) : (
        <>
          {/* Upcoming */}
          {upcoming.length > 0 && (
            <div className="space-y-2" data-testid="timeline-upcoming">
              <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.09em] text-[#8A6D00]">
                <CalendarClock className="w-3.5 h-3.5" /> Upcoming
              </div>
              {upcoming.map((e) => <Card key={e.id} e={e} />)}
            </div>
          )}

          {groups.length === 0 && upcoming.length === 0 ? (
            <div className="rounded-lg border border-[#E6E2D9] bg-[#FBFAF7] px-6 py-10 text-center" data-testid="timeline-empty">
              <p className="text-[14px] font-semibold text-[#1A1A1A]">No activity yet</p>
              <p className="text-[13px] text-[#59554D] mt-1">Log a touch to start the timeline.</p>
            </div>
          ) : groups.map((g) => (
            <div key={g.key} className="space-y-2" data-testid={`timeline-month-${g.key}`}>
              <div className="text-[12px] font-semibold text-[#6B6660] pt-1">{g.key}</div>
              {g.items.map((e) => <Card key={e.id} e={e} />)}
            </div>
          ))}
        </>
      )}
    </div>
  );
}
